import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { rg2GridView } from 'script/cpmRealgrid2.common';

const CREAM_URL = 'https://cream.methodia.co.kr';

// ── 토큰 관련 ──────────────────────────────────────────────────────────────

function decodeJWT(token) {
    try {
        if (!token) return null;
        var parts = token.split('.');
        if (parts.length !== 3) return null;
        var payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        var decoded = decodeURIComponent(
            window.atob(payload).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join('')
        );
        return JSON.parse(decoded);
    } catch (e) {
        return null;
    }
}

function getCreamToken() {
    var token = sessionStorage.getItem('jwt');
    if (token && token.length > 10) return token;
    token = localStorage.getItem('jwt');
    if (token && token.length > 10) return token;
    var cookies = document.cookie.split(';');
    for (var i = 0; i < cookies.length; i++) {
        var parts = cookies[i].trim().split('=');
        if (parts.length >= 2 && parts[0] === 'MDA_JWT') {
            token = parts.slice(1).join('=');
            if (token && token.length > 10) return token;
        }
    }
    return null;
}

function refreshCreamToken(callback) {
    try {
        var xhr = new XMLHttpRequest();
        xhr.open('POST', '/sys/login/refreshToken.do', true);
        xhr.withCredentials = true;
        xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
        xhr.onreadystatechange = function () {
            if (xhr.readyState === 4) {
                try {
                    var parser = new DOMParser();
                    var xmlDoc = parser.parseFromString(xhr.responseText, 'text/xml');
                    var successEl = xmlDoc.getElementsByTagName('success')[0];
                    var tokenEl = xmlDoc.getElementsByTagName('token')[0];
                    var success = successEl && (successEl.textContent || '').toLowerCase() === 'true';
                    var token = tokenEl ? (tokenEl.textContent || '') : '';
                    if (success && token && token.length > 10) {
                        sessionStorage.setItem('jwt', token);
                        if (localStorage.getItem('jwt')) localStorage.setItem('jwt', token);
                        if (callback) callback(token);
                    } else {
                        if (callback) callback(null);
                    }
                } catch (e) {
                    if (callback) callback(null);
                }
            }
        };
        xhr.onerror = function () { if (callback) callback(null); };
        xhr.send();
    } catch (e) {
        if (callback) callback(null);
    }
}

// ── 컨텐츠 추출 ────────────────────────────────────────────────────────────

function getPageTitle() {
    try {
        var titleEl = document.querySelector('.page-title-box h4, .page-title, h4.mb-sm-0');
        if (titleEl && titleEl.textContent && titleEl.textContent.trim()) return titleEl.textContent.trim();
        return document.title || 'CPM';
    } catch (e) {
        return document.title || 'CPM';
    }
}

function extractGridDataFromWindow() {
    var gridTexts = [];
    try {
        var gridIds = Object.keys(rg2GridView || {});
        var extractedDPs = [];
        gridIds.forEach(function (gridId) {
            try {
                var gridView = rg2GridView[gridId];
                if (!gridView || typeof gridView.getDataSource !== 'function') return;
                var dp = gridView.getDataSource();
                if (!dp || typeof dp.getRowCount !== 'function') return;
                if (extractedDPs.indexOf(dp) !== -1) return;
                extractedDPs.push(dp);
                var rowCount = dp.getRowCount();
                if (rowCount <= 0) return;
                var fields = [];
                if (typeof dp.getFieldCount === 'function') {
                    var fCount = dp.getFieldCount();
                    for (var fi = 0; fi < fCount; fi++) {
                        var fInfo = typeof dp.getField === 'function' ? dp.getField(fi) : null;
                        fields.push(fInfo && fInfo.fieldName ? fInfo.fieldName : ('col' + fi));
                    }
                }
                var data = [];
                for (var ri = 0; ri < Math.min(rowCount, 5000); ri++) {
                    if (typeof dp.getJsonRow === 'function') {
                        var jRow = dp.getJsonRow(ri);
                        if (jRow) { data.push(jRow); continue; }
                    }
                    if (fields.length > 0 && typeof dp.getRow === 'function') {
                        var rawRow = dp.getRow(ri);
                        if (Array.isArray(rawRow)) {
                            var obj = {};
                            fields.forEach(function (f, idx) { obj[f] = rawRow[idx]; });
                            data.push(obj);
                        }
                    }
                }
                if (data.length > 0) {
                    var header = Object.keys(data[0] || {}).join('\t');
                    var rows = data.map(function (row) {
                        return Object.values(row || {}).join('\t');
                    }).join('\n');
                    gridTexts.push('[Grid:' + gridId + ']\n' + (header ? header + '\n' : '') + rows);
                }
            } catch (e) {}
        });
    } catch (e) {}
    return gridTexts.join('\n\n');
}

function getPageContent() {
    try {
        // React SPA이므로 현재 DOM에서 직접 추출
        var selectors = ['.page-content', '.container-fluid', 'main', '.main-content'];
        for (var i = 0; i < selectors.length; i++) {
            var el = document.querySelector(selectors[i]);
            if (el) {
                var txt = (el.innerText || el.textContent || '').trim();
                if (txt && txt.length > 10) return txt;
            }
        }
        return '[title] ' + (document.title || document.location.href || '');
    } catch (e) {
        return null;
    }
}

function getFullPageContent() {
    try {
        var base = getPageContent() || '';
        var gridData = extractGridDataFromWindow();
        if (gridData) return base + '\n\n[전체 그리드 데이터 (숨김 행 포함)]\n' + gridData;
        return base;
    } catch (e) {
        return getPageContent();
    }
}

// ── 컴포넌트 ───────────────────────────────────────────────────────────────

const CreamChatbot = () => {
    const location = useLocation();
    const creamLoadedRef = useRef(false);
    const refreshTimerRef = useRef(null);
    const contentCacheRef = useRef({ content: null, ts: 0 });

    function isCreamOpen() {
        var el = document.getElementById('offcanvasCreamCpm');
        return el && el.classList.contains('show');
    }

    function getCreamOrigin() {
        try { return new URL(CREAM_URL).origin; } catch (e) { return '*'; }
    }

    function postToIframe(data) {
        try {
            var iframe = document.getElementById('offcanvasCreamFrameCpm');
            if (!iframe || !iframe.contentWindow) return;
            try {
                iframe.contentWindow.postMessage(data, getCreamOrigin());
            } catch (e) {
                iframe.contentWindow.postMessage(data, '*');
            }
        } catch (e) {}
    }

    function sendTokenMessage(token) {
        var tokenData = token ? decodeJWT(token) : null;
        postToIframe({
            type: 'cream_set_token',
            token: token,
            userNm: tokenData ? tokenData.userNm : '',
            userId: tokenData ? tokenData.userId : '',
            compTypeCd: tokenData ? tokenData.compTypeCd : '',
            compId: tokenData ? tokenData.compId : '',
            athrCd: tokenData ? tokenData.athrCd : '',
            exp: tokenData ? tokenData.exp : null,
            iat: tokenData ? tokenData.iat : null
        });
    }

    function scheduleTokenRefresh() {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
        var token = getCreamToken();
        if (!token) return;
        var data = decodeJWT(token);
        if (!data || !data.exp) return;
        var now = Math.floor(Date.now() / 1000);
        var remainSec = data.exp - now;
        if (remainSec <= 0) {
            refreshCreamToken(function (newToken) {
                if (newToken) {
                    scheduleTokenRefresh();
                    if (creamLoadedRef.current) sendTokenMessage(newToken);
                }
            });
        } else {
            var refreshInMs = Math.max(0, (remainSec - 300)) * 1000;
            refreshTimerRef.current = setTimeout(function () {
                refreshCreamToken(function (newToken) {
                    if (newToken) {
                        scheduleTokenRefresh();
                        if (creamLoadedRef.current) sendTokenMessage(newToken);
                    }
                });
            }, refreshInMs);
        }
    }

    function loadOffcanvasContent() {
        var iframe = document.getElementById('offcanvasCreamFrameCpm');
        if (!iframe) return;

        if (creamLoadedRef.current) {
            var token = getCreamToken();
            var tokenData = token ? decodeJWT(token) : null;
            var isExpired = tokenData && tokenData.exp && (tokenData.exp < Math.floor(Date.now() / 1000));
            if (isExpired) {
                refreshCreamToken(function (newToken) {
                    if (newToken && iframe.contentWindow) sendTokenMessage(newToken);
                    setTimeout(sendContextInfo, 300);
                });
            } else if (token) {
                sendTokenMessage(token);
                setTimeout(sendContextInfo, 300);
            } else {
                setTimeout(sendContextInfo, 300);
            }
            return;
        }

        var token = getCreamToken();
        if (token) {
            var checkData = decodeJWT(token);
            if (checkData && checkData.exp && checkData.exp < Math.floor(Date.now() / 1000)) {
                refreshCreamToken(function (newToken) {
                    if (newToken) loadOffcanvasContent();
                });
                return;
            }
        }
        doLoadIframe(iframe, token);
    }

    function doLoadIframe(iframe, token) {
        var tokenData = token ? decodeJWT(token) : null;
        var messageData = {
            type: 'cream_set_token',
            token: token,
            userNm: tokenData ? tokenData.userNm : '',
            userId: tokenData ? tokenData.userId : '',
            compTypeCd: tokenData ? tokenData.compTypeCd : '',
            compId: tokenData ? tokenData.compId : '',
            athrCd: tokenData ? tokenData.athrCd : '',
            exp: tokenData ? tokenData.exp : null,
            iat: tokenData ? tokenData.iat : null
        };

        iframe.onload = function () {
            var src = iframe.src || '';
            if (src.indexOf('/login') === -1 && src.indexOf(new URL(CREAM_URL).hostname) > -1) {
                creamLoadedRef.current = true;
            }
            try { iframe.contentWindow.postMessage(messageData, getCreamOrigin()); } catch (e) {}
            setTimeout(function () {
                try { iframe.contentWindow.postMessage(messageData, getCreamOrigin()); } catch (e) {}
            }, 500);
            setTimeout(function () {
                try { iframe.contentWindow.postMessage(messageData, getCreamOrigin()); } catch (e) {}
            }, 1500);
        };

        iframe.src = CREAM_URL;
    }

    function sendContextInfo() {
        if (!isCreamOpen()) return;
        contentCacheRef.current = { content: null, ts: 0 };
        var title = getPageTitle();
        postToIframe({ type: 'cream_context_info', title: title, windowId: null });
    }

    // ── 레이아웃 밀기 ────────────────────────────────────────────────────────

    function pushLayout(open) {
        var wrapper = document.getElementById('layout-wrapper');
        if (!wrapper) return;
        var oc = document.getElementById('offcanvasCreamCpm');
        if (open) {
            var currentWidth = (oc && oc.style.width) ? oc.style.width : '550px';
            document.documentElement.style.setProperty('--cream-cpm-width', currentWidth);
            wrapper.classList.add('layout-wrapper-pushed');
        } else {
            wrapper.classList.remove('layout-wrapper-pushed');
        }
        setTimeout(function () { window.dispatchEvent(new Event('resize')); }, 50);
    }

    // ── message 이벤트 핸들러 ─────────────────────────────────────────────

    useEffect(function () {
        function onMessage(event) {
            try {
                var data = event.data;
                if (!data) return;

                if (data.type === 'cream_request_token') {
                    var token = getCreamToken();
                    var tokenData = token ? decodeJWT(token) : null;
                    var isExpired = tokenData && tokenData.exp && (tokenData.exp < Math.floor(Date.now() / 1000));
                    if (isExpired) {
                        refreshCreamToken(function (newToken) { if (newToken) sendTokenMessage(newToken); });
                    } else if (token) {
                        sendTokenMessage(token);
                    }
                }

                if (data.type === 'cream_request_content') {
                    try {
                        var iframe2 = document.getElementById('offcanvasCreamFrameCpm');
                        if (iframe2 && iframe2.contentWindow) {
                            var origin = event.origin !== 'null' ? event.origin : getCreamOrigin();
                            iframe2.contentWindow.postMessage({
                                type: 'cream_context_info',
                                title: getPageTitle(),
                                windowId: null
                            }, origin);
                        }
                    } catch (e) {}
                }

                if (data.type === 'cream_request_content_read') {
                    setTimeout(function () {
                        try {
                            var rdOffset = parseInt(data.offset) || 0;
                            var rdChunkSz = parseInt(data.chunkSize) || 8000;
                            var now = Date.now();
                            if (!contentCacheRef.current.content || (now - contentCacheRef.current.ts) > 10000) {
                                contentCacheRef.current = { content: getFullPageContent() || '', ts: now };
                            }
                            var cached = contentCacheRef.current.content;
                            var chunk = cached.substring(rdOffset, rdOffset + rdChunkSz);
                            var hasMore = (rdOffset + rdChunkSz) < cached.length;
                            var iframe3 = document.getElementById('offcanvasCreamFrameCpm');
                            if (iframe3 && iframe3.contentWindow) {
                                var origin2 = event.origin !== 'null' ? event.origin : getCreamOrigin();
                                iframe3.contentWindow.postMessage({
                                    type: 'cream_content_chunk',
                                    content: chunk,
                                    totalLength: cached.length,
                                    offset: rdOffset,
                                    hasMore: hasMore,
                                    windowId: null,
                                    title: getPageTitle()
                                }, origin2);
                            }
                        } catch (e) {}
                    }, 0);
                }
            } catch (e) {}
        }

        window.addEventListener('message', onMessage, false);
        return function () { window.removeEventListener('message', onMessage, false); };
    }, []);

    // ── offcanvas 이벤트 바인딩 ──────────────────────────────────────────

    useEffect(function () {
        var oc = document.getElementById('offcanvasCreamCpm');
        if (!oc) return;

        function onShow() {
            pushLayout(true);
            try { scheduleTokenRefresh(); } catch (e) {}
            loadOffcanvasContent();
        }

        function onShown() {
            var delay = creamLoadedRef.current ? 300 : 800;
            setTimeout(function () {
                sendContextInfo();
            }, delay);
        }

        function onHidden() {
            pushLayout(false);
            var iframe = document.getElementById('offcanvasCreamFrameCpm');
            if (iframe) {
                var src = iframe.src || '';
                try {
                    if (src.indexOf('/login') > -1 || src.indexOf(new URL(CREAM_URL).hostname) === -1) {
                        creamLoadedRef.current = false;
                    }
                } catch (e) {}
            }
        }

        oc.addEventListener('show.bs.offcanvas', onShow);
        oc.addEventListener('shown.bs.offcanvas', onShown);
        oc.addEventListener('hidden.bs.offcanvas', onHidden);

        return function () {
            oc.removeEventListener('show.bs.offcanvas', onShow);
            oc.removeEventListener('shown.bs.offcanvas', onShown);
            oc.removeEventListener('hidden.bs.offcanvas', onHidden);
        };
    }, []);

    // ── 라우트 변경 시 컨텍스트 전송 ─────────────────────────────────────

    useEffect(function () {
        if (isCreamOpen()) {
            contentCacheRef.current = { content: null, ts: 0 };
            setTimeout(sendContextInfo, 300);
        }
    }, [location.pathname]);

    // ── 드래그 리사이즈 ──────────────────────────────────────────────────

    useEffect(function () {
        var ZONE = 24;
        var MIN_WIDTH = 300;
        var MAX_RATIO = 0.8;
        var MAX_WIDTH = 860;
        var resizing = false;
        var startX = 0;
        var startWidth = 0;

        function getOC() { return document.getElementById('offcanvasCreamCpm'); }
        function getHandle() { return document.getElementById('offcanvasCreamCpmResizeHandle'); }
        function isShown() { var el = getOC(); return el && el.classList.contains('show'); }

        function setPointerEvents(enable) {
            var fr = document.getElementById('offcanvasCreamFrameCpm');
            if (fr) fr.style.pointerEvents = enable ? '' : 'none';
        }

        function startResize(e) {
            if (!isShown()) return;
            var el = getOC();
            resizing = true;
            startX = e.clientX;
            startWidth = el.offsetWidth;
            el.classList.add('cream-resizing');
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';
            setPointerEvents(false);
            e.preventDefault();
            e.stopPropagation();
        }

        var handle = getHandle();
        if (handle) handle.addEventListener('mousedown', startResize, true);

        function onMouseMove(e) {
            var el = getOC();
            if (!el) return;
            if (resizing) {
                var maxW = Math.min(Math.round(window.innerWidth * MAX_RATIO), MAX_WIDTH);
                var newW = startWidth + (startX - e.clientX);
                if (newW < MIN_WIDTH) newW = MIN_WIDTH;
                if (newW > maxW) newW = maxW;
                el.style.width = newW + 'px';
                document.documentElement.style.setProperty('--cream-cpm-width', newW + 'px');
                return;
            }
            if (!isShown()) {
                if (document.body.style.cursor === 'ew-resize') document.body.style.cursor = '';
                return;
            }
            var rect = el.getBoundingClientRect();
            var dist = e.clientX - rect.left;
            if (dist >= 0 && dist <= ZONE) {
                document.body.style.cursor = 'ew-resize';
            } else {
                if (document.body.style.cursor === 'ew-resize') document.body.style.cursor = '';
            }
        }

        function onMouseDown(e) {
            if (!isShown() || resizing) return;
            var el = getOC();
            var rect = el.getBoundingClientRect();
            var dist = e.clientX - rect.left;
            if (dist < 0 || dist > ZONE) return;
            startResize(e);
        }

        function onMouseUp(e) {
            if (!resizing) return;
            resizing = false;
            var el = getOC();
            if (el) el.classList.remove('cream-resizing');
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            setPointerEvents(true);
            if (e) { e.preventDefault(); e.stopPropagation(); }
        }

        function onBlur() {
            if (!resizing) return;
            resizing = false;
            var el = getOC();
            if (el) el.classList.remove('cream-resizing');
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            setPointerEvents(true);
        }

        document.addEventListener('mousemove', onMouseMove, true);
        document.addEventListener('mousedown', onMouseDown, true);
        document.addEventListener('mouseup', onMouseUp, true);
        window.addEventListener('blur', onBlur);

        return function () {
            if (handle) handle.removeEventListener('mousedown', startResize, true);
            document.removeEventListener('mousemove', onMouseMove, true);
            document.removeEventListener('mousedown', onMouseDown, true);
            document.removeEventListener('mouseup', onMouseUp, true);
            window.removeEventListener('blur', onBlur);
            if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        };
    }, []);

    return (
        <>
            {/* offcanvas 챗봇 패널 */}
            <div
                className="offcanvas offcanvas-end border-0"
                id="offcanvasCreamCpm"
                tabIndex="-1"
                data-bs-backdrop="false"
                data-bs-scroll="true"
                style={{ width: '550px', zIndex: 1045 }}
            >
                {/* 드래그 리사이즈 핸들 */}
                <div
                    id="offcanvasCreamCpmResizeHandle"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: '-6px',
                        width: '20px',
                        height: '100%',
                        cursor: 'ew-resize',
                        zIndex: 1060,
                        background: 'transparent'
                    }}
                />
                <iframe
                    id="offcanvasCreamFrameCpm"
                    name="offcanvasCreamFrameCpm"
                    title="Cream+AI"
                    style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                />
            </div>

            {/* 레이아웃 밀기 + offcanvas 스타일 */}
            <style>{`
                /* AI 챗봇 버튼 그라데이션 */
                .btn-animated {
                    position: relative;
                    overflow: hidden;
                    border-radius: 50% !important;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 39px !important;
                    height: 39px !important;
                    padding: 0;
                    background-color: #5b3fbd !important;
                    background-image: linear-gradient(
                        135deg,
                        #72ecff 0%,
                        #4d8aff 34%,
                        #6e4fd5 70%,
                        #c35ffb 100%
                    ) !important;
                    background-position: center center !important;
                    background-repeat: no-repeat !important;
                    border: none !important;
                    color: #ffffff !important;
                    box-sizing: border-box;
                }
                .btn-animated::before,
                .btn-animated::after { content: none; }
                .btn-animated .ai-icon-svg {
                    filter: brightness(0) invert(1);
                    transition: filter 0.2s ease;
                }
                .btn-animated:hover,
                .btn-animated:focus {
                    background-image: linear-gradient(
                        135deg,
                        #c35ffb 0%,
                        #6e4fd5 34%,
                        #4d8aff 70%,
                        #72ecff 100%
                    ) !important;
                    background-color: #5b3fbd !important;
                    box-shadow: 0 0 10px rgba(91, 63, 189, 0.45) !important;
                }
                .ai-icon-svg { filter: brightness(0) saturate(100%); }
                [data-topbar="dark"] .ai-icon-svg { filter: brightness(0) saturate(100%) invert(1); }

                #layout-wrapper,
                #page-topbar,
                .footer,
                .main-content {
                    transition: margin-right 0.3s ease, width 0.3s ease !important;
                }
                #offcanvasCreamCpm.offcanvas {
                    box-shadow: -2px 0 10px rgba(0,0,0,0.1);
                    transition: transform 0.3s ease, width 0.3s ease;
                    transform: none !important;
                    visibility: visible !important;
                    background-color: #fff;
                    top: 50px !important;
                    height: calc(100% - 50px) !important;
                    max-width: 95vw;
                }
                #offcanvasCreamCpm.offcanvas.hiding,
                #offcanvasCreamCpm.offcanvas:not(.show) {
                    transform: translateX(100%) !important;
                }
                #offcanvasCreamCpm.show::before {
                    content: '';
                    position: absolute;
                    left: 0; top: 0;
                    width: 5px; height: 100%;
                    background: rgba(101, 31, 255, 0.20);
                    pointer-events: none;
                    z-index: 10;
                }
                #offcanvasCreamCpm.cream-resizing {
                    transition: none !important;
                }
                #offcanvasCreamCpm.cream-resizing::before {
                    background: rgba(101, 31, 255, 0.50);
                }
                [data-layout="vertical"] .layout-wrapper-pushed .main-content {
                    margin-right: var(--cream-cpm-width, 550px) !important;
                    width: calc(100% - 250px - var(--cream-cpm-width, 550px)) !important;
                }
                [data-layout="vertical"][data-sidebar-size="sm-hover"] .layout-wrapper-pushed .main-content,
                [data-layout="vertical"][data-sidebar-size="sm"] .layout-wrapper-pushed .main-content {
                    width: calc(100% - 70px - var(--cream-cpm-width, 550px)) !important;
                }
                @media (max-width: 767.98px) {
                    [data-layout="vertical"] .layout-wrapper-pushed .main-content {
                        width: calc(100% - var(--cream-cpm-width, 550px)) !important;
                        margin-left: 0 !important;
                    }
                    #offcanvasCreamCpm { width: 100vw !important; }
                }
                @media (max-width: 1200px) {
                    #offcanvasCreamCpm { width: 550px; }
                }
            `}</style>
        </>
    );
};

export default CreamChatbot;
